import React from 'react'
import { List, ListItem, Button, CircularProgress, Box, Typography } from '@mui/material'
import Alert from '@mui/material/Alert'
import StockCardPremixItem from 'components/stockCardItems/StockCardPremixItem'
import { IStockCard, IStockCardProducts } from 'interfaces/IStockCard'
import { STOCK_CARD_TYPE_HALF_COMPLETE, VALUE_IN } from 'enums/stockCard'
import { cleanCorruptedString } from 'components/production/stockCardUtils'
import { Department } from 'enums/department'

interface Props {
  stockCardData: IStockCard
  inOutDirection: string
  latestStock: Array<number | null>
  onChange: (
    index: number,
    value: any,
    key: string
  ) => void
  onConfirm: () => void
  isLoading: boolean
  getValidationMessage?: (stockCard: IStockCard) => string | null
  hideSummary?: boolean
  isSuperuser?: boolean
  onUpdate?: () => void
  isUpdating?: boolean
  onDelete?: () => void
  isEditing: boolean
  setIsEditing: (isEditing: boolean) => void
  onAddProduct?: () => void
  onRemoveProduct?: (index: number) => void
  onPrefill?: () => void
  createdAtLabel?: string
  getMatchingBatch?: (item: { batchNumber?: string | null }) => any
  isBatchLoading?: boolean
  autoFormatQuantity?: boolean
  department?: Department
  getProductBatchOptions?: (product: IStockCardProducts) => Array<{
    batchNumber: string
    productStock: number
    currentQuantity?: number
    expiryDate?: string | null
    productionDate?: string | null
  }>
}

export default function HalfCompleteDialog(props: Props) {
  const {
    stockCardData,
    inOutDirection,
    latestStock,
    onChange,
    onConfirm,
    isLoading,
    getValidationMessage,
    hideSummary,
    isSuperuser,
    onUpdate,
    isUpdating,
    onDelete,
    onAddProduct,
    onRemoveProduct,
    onPrefill,
    isEditing,
    setIsEditing,
    createdAtLabel,
    getMatchingBatch,
    isBatchLoading,
    autoFormatQuantity,
    department,
    getProductBatchOptions,
  } = props


  const canEdit = isSuperuser
  const validationMessage = getValidationMessage?.(stockCardData)

  const isIncomingHalfComplete =
    inOutDirection === VALUE_IN &&
    stockCardData.type === STOCK_CARD_TYPE_HALF_COMPLETE

  const confirmedQtyLabel = isIncomingHalfComplete
    ? 'Quantity STJ Terbuat'
    : undefined
  const fillingLabel = isIncomingHalfComplete
    ? 'Konfirmasi Filling Amount'
    : undefined

  return (
    <List>
      {canEdit && (
        <ListItem>
          <Box width="100%" display="flex" justifyContent="flex-end" gap={8}>
            {onPrefill && (isEditing || !stockCardData.isConfirmed) && (
              <Button
                variant="contained"
                color="secondary"
                onClick={onPrefill}
                disabled={isLoading}
              >
                Isi Semua Sesuai Jumlah Diminta
              </Button>
            )}
            <Button
              variant="outlined"
              color={isEditing ? 'secondary' : 'primary'}
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? 'Cancel Edit' : 'Edit'}
            </Button>
          </Box>
        </ListItem>
      )}
      {stockCardData.stockCardProducts.map((stockCardProduct, index) => (
        <ListItem key={index} divider={index !== stockCardData.stockCardProducts.length - 1}>
          <StockCardPremixItem
            stockCardProduct={stockCardProduct}
            inOutDirection={inOutDirection}
            currentStock={latestStock[index]}
            outgoingDestination={stockCardData.outgoingDestination}
            onChange={(val, key) => onChange(index, val, key)}
            disabled={!!stockCardData.isConfirmed && !isEditing}
            hideSummary={hideSummary}
            index={index}
            isSuperuser={isSuperuser}
            isConfirmed={!!stockCardData.isConfirmed}
            isEditing={isEditing}
            onRemove={isEditing && onRemoveProduct ? () => onRemoveProduct(index) : undefined}
            brandId={(stockCardProduct.brandId || stockCardProduct.product?.brandId || stockCardData.brandId) as string}
            packagingName={cleanCorruptedString(stockCardData.packagingName as string)}
            packagingId={stockCardData.packagingId as string}
            pictureUrl={stockCardData.pictureUrl as string | null}
            labelId={(stockCardProduct.labelId || stockCardProduct.product?.['labelId'] || stockCardData.labelId) as string}
            notes={stockCardData.notes as string}
            createdAt={stockCardData.createdAt as string}
            createdAtLabel={createdAtLabel}
            confirmedQtyLabel={confirmedQtyLabel}
            fillingLabel={fillingLabel}
            getMatchingBatch={getMatchingBatch}
            isBatchLoading={isBatchLoading}
            autoFormatQuantity={autoFormatQuantity}
            department={department}
            batchOptions={getProductBatchOptions?.(stockCardProduct)}
          />
        </ListItem>
      ))}
      {isEditing && onAddProduct && (
        <ListItem>
          <Button variant="outlined" color="primary" onClick={onAddProduct}>
            Add Product
          </Button>
        </ListItem>
      )}
      <ListItem>
        <Box display="flex" gap={8}>
          {isEditing ? (
            <>
              {onUpdate && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onUpdate}
                  disabled={isUpdating}
                  startIcon={
                    isUpdating ? <CircularProgress size={18} color="inherit" /> : null
                  }
                >
                  Update Stock Card
                </Button>
              )}
              {canEdit && onDelete && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onDelete}
                  disabled={isLoading || !!stockCardData.isConfirmed}
                >
                  Delete
                </Button>
              )}
            </>
          ) : (
            <>
              {!stockCardData.isConfirmed && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onConfirm}
                  disabled={
                    isLoading ||
                    (!!validationMessage && !stockCardData.isConfirmed)
                  }
                  startIcon={
                    isLoading ? <CircularProgress size={18} color="inherit" /> : null
                  }
                >
                  Save
                </Button>
              )}
            </>
          )}
        </Box>
      </ListItem>
      {isEditing && !!stockCardData.isConfirmed && (
        <ListItem>
          <Typography color="secondary" variant="body2">
            Kartu stok tidak dapat dihapus karena sudah dikonfirmasi.
          </Typography>
        </ListItem>
      )}
      {validationMessage &&
        !stockCardData.isConfirmed && (
          <div style={{ padding: '0 16px' }}>
            <Alert severity="warning">{validationMessage}</Alert>
          </div>
        )}
    </List>
  );
}
